import cn from 'classnames'
import type {KeyboardEvent} from 'react'
import {useId} from 'react'
import {SLIDER_POSITION_MAX} from 'slices/RoiCalculator/constants'
import type {SliderConfig} from 'slices/RoiCalculator/types'
import {positionFromValue, stepValue, valueFromPosition} from 'slices/RoiCalculator/utils'

type KeyStep = {direction: 1 | -1; steps: number}

const PAGE_STEPS = 10

const KEY_STEPS: Partial<Record<string, KeyStep>> = {
  ArrowRight: {direction: 1, steps: 1},
  ArrowUp: {direction: 1, steps: 1},
  ArrowLeft: {direction: -1, steps: 1},
  ArrowDown: {direction: -1, steps: 1},
  PageUp: {direction: 1, steps: PAGE_STEPS},
  PageDown: {direction: -1, steps: PAGE_STEPS},
}

const THUMB_SIZE_PX = 18

const RANGE_INPUT_CLASS_NAME = cn(
  'relative h-6 w-full cursor-pointer appearance-none bg-transparent focus-visible:outline-none',
  '[&::-moz-range-track]:bg-transparent [&::-webkit-slider-runnable-track]:bg-transparent',
  '[&::-webkit-slider-thumb]:size-[18px] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-green-dark',
  '[&::-moz-range-thumb]:size-[18px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-green-dark',
  'focus-visible:[&::-webkit-slider-thumb]:ring-2 focus-visible:[&::-webkit-slider-thumb]:ring-green-dark focus-visible:[&::-webkit-slider-thumb]:ring-offset-2',
  'focus-visible:[&::-moz-range-thumb]:ring-2 focus-visible:[&::-moz-range-thumb]:ring-green-dark focus-visible:[&::-moz-range-thumb]:ring-offset-2',
)

export type SliderProps = {
  label: string
  value: number
  config: SliderConfig
  formatValue: (value: number) => string
  unitSuffix: string
  showPulse?: boolean
  onChange: (value: number) => void
}

export const Slider = ({label, value, config, formatValue, unitSuffix, showPulse = false, onChange}: SliderProps) => {
  const id = useId()
  const position = positionFromValue(value, config)
  const displayedValue = formatValue(value)
  const progress = (position / SLIDER_POSITION_MAX) * 100

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const keyStep = KEY_STEPS[event.key]

    if (!keyStep) {
      return
    }

    event.preventDefault()
    onChange(stepValue(value, keyStep.direction, config, keyStep.steps))
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-16 leading-none text-moss-grey-550">
          {label}
        </label>
        <div className="flex items-baseline gap-1">
          <span
            aria-hidden="true"
            className="text-[26px] font-semibold leading-none tracking-[-0.01em] text-moss-black"
          >
            {displayedValue}
          </span>
          <span className="text-14 text-moss-grey-550">{unitSuffix}</span>
        </div>
      </div>
      <div className="relative flex h-6 items-center">
        <div className="absolute inset-x-0 h-1.5 rounded-full bg-green-dark/10">
          <div className="h-full rounded-full bg-green-dark" style={{width: `${progress}%`}} />
        </div>
        {showPulse && (
          <span
            aria-hidden="true"
            data-testid="slider-pulse"
            className="pointer-events-none absolute top-1/2 size-[18px] -translate-x-1/2 -translate-y-1/2"
            style={{left: `calc(${progress}% + ${THUMB_SIZE_PX / 2 - (progress / 100) * THUMB_SIZE_PX}px)`}}
          >
            <span className="block size-full rounded-full bg-green-dark opacity-0 motion-safe:animate-roi-thumb-ripple" />
          </span>
        )}
        <input
          id={id}
          type="range"
          min={0}
          max={SLIDER_POSITION_MAX}
          step={1}
          value={position}
          aria-valuetext={displayedValue}
          onChange={(event) => onChange(valueFromPosition(Number(event.target.value), config))}
          onKeyDown={handleKeyDown}
          className={RANGE_INPUT_CLASS_NAME}
        />
      </div>
    </div>
  )
}
